import { createContext, useContext, useMemo, useState } from "react"
const FondoContext = createContext()

/**
 * Proveedor de contexto para la gestión de estado relacionado con solicitudes y aprobación de vacaciones.
 *
 * @component
 * @param {Object} props - Propiedades del componente.
 * @param {React.ReactNode} props.children - Componentes hijos que tendrán acceso al contexto.
 * @param {boolean} props.reloadT - Estado externo de recarga.
 * @param {(value: boolean) => void} props.setReloadT - Setter para `reloadT`.
 * @returns {JSX.Element} Un proveedor de contexto con valores y funciones para el manejo de vacaciones.
 *
 * @example
 * <VacationProvider reloadT={reloadT} setReloadT={setReloadT}>
 *   <App />
 * </VacationProvider>
 */
export function VacationProvider({ children, reloadT, setReloadT }) {
	const [vacation, setVacation] = useState({
		colaborador: null,
		available_days: null,
		status: null,
	})
	const [reload, setReload] = useState(false)
	const [openApprove, setOpenApprove] = useState(false)
	const [openRequest, setOpenRequest] = useState(false)

	const datos = useMemo(
		() => ({
			reload,
			reloadT,
			openApprove,
			setOpenApprove,
			openRequest,
			setOpenRequest,
			vacation,
			setVacation,

			refresh: () => {
				setReload(!reload)
				if (typeof setReloadT === "function") setReloadT(!reloadT)
			},
		}),
		[reload, reloadT, openApprove, openRequest, vacation, setReloadT]
	)
	return <FondoContext.Provider value={datos}>{children}</FondoContext.Provider>
}

/**
 * @typedef {Object} VacationData
 * @property {Object} id - Solicitud de vacaciones.
 * @property {Object} colaborador - Información del colaborador relacionado a las vacaciones.
 * @property {number} available_days - Número de días restantes de vacaciones.
 * @property {number} date_start - Fecha de inicio de la solicitud de vacaciones.
 * @property {number} date_end - Fecha de fin de la solicitud de vacaciones.
 * @property {number} days - Número de días solicitados.
 * @property {number} status - Estado de la solicitud de vacaciones.
 *
 *
 */

/**
 * @typedef {Object} VacationContextValue
 * @property {boolean} reload - Estado que indica si se debe recargar la información.
 * @property {boolean} reloadT - Estado externo para recarga (controlado por `setReloadT`).
 * @property {boolean} openApprove - Estado que indica si el modal de aprobación está abierto.
 * @property {(value: boolean) => void} setOpenApprove - Setter para `openApprove`.
 * @property {boolean} openRequest - Estado que indica si el modal de solicitud está abierto.
 * @property {(value: boolean) => void} setOpenRequest - Setter para `openRequest`.
 * @property {VacationData} vacation - Datos generales de vacaciones.
 * @property {(value: Object) => void} setVacation - Setter para `vacation`.
 * @property {() => void} refresh - Función que alterna `reload` y `reloadT` para forzar recargas.
 */

/**
 * Hook para acceder al contexto de vacaciones.
 *
 * @returns {VacationContextValue} Valores y funciones del contexto de vacaciones.
 *
 * @example
 * const { solicitud, setSolicitud, refresh } = UseVacationContext()
 */
export function UseVacationContext() {
	return useContext(FondoContext)
}
